var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import globToRegExp from 'glob-to-regexp';
import { deserializeResponse, isStale, isExpired, getDynamicCacheName } from './service-worker-utils';
import { ExternalResolvePromise } from '../models/external-resolve-promise';
/**
 * Makes a fetch request to the service worker, then uses the callback with the successfully returned response payload. When there is an error the error callback is used.
 * The service worker caches anything that is properly registered with the service worker.
 * If the data is fresh then we return data from the cache.
 * If the data is stale, the callback is called twice. Once with old data, and then again with fresh data once it is received over the network
 * If the data is expired then we can't use the cached data at all and make a network call
 * @param url the url used to make the request
 * @param callBack a function called with the response payload
 * @param init fetch options object
 * @param errorCallback optional callback that will be run if any of the fetch's fail. We add a property called
 * isDisplayingStaleData to the error object that is set to true when we show stale data from the cache, but fail to get fresh data from the network
 * @param tagList optional list of tags that will allow the cache to be cleared by tag name.  See the function deleteFromCacheByTag(tagMatch: RegExp).
 */
export function superFetch(url, callBack, init = {}, errorCallback, tagList) {
    //NOTE: It is against the better judgement of team Voltron, but we were forced to pass in an Error object into the success callback if an errorCallback was not provided.
    //The best way to handle this would be if (responsePayload instanceof Error) { } else { must be a normal payload }
    //If an errorCallback is not provided, the response body goes in whether the status code is 200 or 500. The only way to figure out the status code is for the API to put the statusCode into the payload.
    //The Voltron team recommends against doing this!!!!  Please just use the errorCallback function.
    if (navigator.serviceWorker.controller == null) {
        //There is no service worker.  We just need to run regular fetch and return the results.
        handleFetchWithoutServiceWorker(url, callBack, init, errorCallback);
        return;
    }
    if (init.headers) {
        init.headers.superFetch = 'true';
        init.headers.superFetchTagList = JSON.stringify(tagList || []);
    }
    else {
        init.headers = {
            superFetch: 'true',
            superFetchTagList: JSON.stringify(tagList || [])
        };
    }
    //Prefetch will return the data in the response if
    // 1) The data is already cached and not expired.
    // 2) The network call is already in progress.
    // We do NOT want preFetch to kick off network calls.
    // If prefetch responds saying that there isn't a cached response, then the callback will kick off a normal fetch operation.
    const formattedUrl = new URL(url, window.location.origin);
    preFetch(formattedUrl.href, tagList).then((serviceWorkerPrefetchMessage) => {
        let prefetchPayload;
        let prefetchResponse;
        if (serviceWorkerPrefetchMessage.case === 'DATA_IN_FLIGHT_WITH_FULL_RESPONSE_OBJECT_RETURNED') {
            prefetchResponse = deserializeResponse(serviceWorkerPrefetchMessage.payload);
        }
        else {
            prefetchPayload = serviceWorkerPrefetchMessage.payload;
        }
        if (prefetchResponse != null) {
            if (prefetchResponse.status >= 400) {
                if (errorCallback != null) {
                    // If there is an error response and an errorcallback let it be handled in the error callback
                    errorCallback(prefetchResponse);
                }
                else {
                    // If we can't get the body like normal just call the callback to prevent regression
                    callBack(prefetchResponse);
                }
            }
            else {
                callBack(prefetchResponse);
            }
        }
        else if (prefetchPayload == null) {
            // This is the case where the call has not yet been made.
            // This is also the case where the call has expired so it returns no data.
            return runFetch();
        }
        else if (serviceWorkerPrefetchMessage.responseIsStale) {
            //This is the case where the data from preFetch came back stale.
            //We return the stale data, and then make sure that
            // 1) The fetch is already in progress
            // 2) If the fetch isn't in progress, it kicks it off.
            const superFetchPayload = timeStampedPayloadToSuperFetchPayload(prefetchPayload);
            callBack(superFetchPayload);
            if (serviceWorkerPrefetchMessage.isInProgress) {
                return preFetch(url, tagList).then((serviceWorkerPrefetchMessage) => {
                    const prefetchPayload = serviceWorkerPrefetchMessage.payload;
                    if (prefetchPayload == null) {
                        console.error('This case should not be hit');
                    }
                    else {
                        const superFetchPayload = timeStampedPayloadToSuperFetchPayload(prefetchPayload);
                        callBack(superFetchPayload);
                    }
                });
            }
            else {
                return runFetch();
            }
        }
        else {
            //This is the case where the data from prefetch came back ready to go. So we return the data.
            const superFetchPayload = timeStampedPayloadToSuperFetchPayload(prefetchPayload);
            callBack(superFetchPayload);
        }
    }).catch((error) => {
        if (errorCallback != null) {
            errorCallback(error);
        }
        else {
            callBack(error);
        }
    });
    // This is a superFetch subroutine that expects a response from the service worker.
    // That's why we can always expect a TimeStampedPayload.
    function runFetch() {
        //If the serviceWorker is set up, the call to fetch will call
        //the callback returned by createFetchInterceptor() which is in service-worker-utils.ts
        return fetch(url, init).then((response) => __awaiter(this, void 0, void 0, function* () {
            const contentType = response.headers.get('content-type');
            if (response.status >= 200 && response.status < 300 && (contentType === null || contentType === void 0 ? void 0 : contentType.indexOf('application/json')) !== -1) {
                const timeStampedPayload = yield response.json();
                const superFetchPayload = timeStampedPayloadToSuperFetchPayload(timeStampedPayload);
                callBack(superFetchPayload);
                // Error Response values
            }
            else if (response.status >= 400) {
                if (errorCallback != null) {
                    // If there is an error response and an errorcallback let it be handled in the error callback
                    errorCallback(response);
                }
                else {
                    // If we can't get the body like normal just call the callback to prevent regression
                    // NOTE: This compatibility feature requires the status code to be in the response body from the API.
                    // Anyone wanting to use this REALLY should consider passing in the error callback instead.
                    response.json().then((o) => { callBack(o); });
                }
                // Other types of responses
            }
            else {
                callBack(response);
            }
        }));
    }
} //end superFetch() function
function handleFetchWithoutServiceWorker(url, callBack, init = {}, errorCallback) {
    // We need to remember to add in the network call timestamp because they will be looking for that property.
    console.warn('Superfetch was used without a service worker.');
    fetch(url, init).then((response) => {
        const contentType = response.headers.get('content-type');
        // Successful JSON type response
        if (response.status >= 200 && response.status < 300 && (contentType === null || contentType === void 0 ? void 0 : contentType.indexOf('application/json')) !== -1) {
            response.json().then((payload) => {
                payload.networkCallTimeStamp = new Date().toJSON();
                callBack(payload);
            });
            // Error Response values
        }
        else if (response.status >= 400) {
            if (errorCallback != null) {
                // If there is an error response and an errorcallback let it be handled in the error callback
                errorCallback(response);
            }
            else {
                // If we can't get the body like normal just call the callback to prevent regression
                // NOTE: This compatibility feature requires the status code to be in the response body from the API.
                // Anyone wanting to use this REALLY should consider passing in the error callback instead.
                response.json().then((o) => { callBack(o); });
            }
            // Other types of responses
        }
        else {
            callBack(response);
        }
    }).catch((error) => {
        // If an error is actually thrown, done on timeouts, for example
        if (errorCallback != null) {
            errorCallback(error);
        }
        else {
            console.error('The following error occured but no errorCallback was provided to superFetch');
            callBack(error);
        }
    });
}
function timeStampedPayloadToSuperFetchPayload(timeStampedPayload) {
    const networkPayload = timeStampedPayload.networkPayload;
    return Object.assign(Object.assign({}, networkPayload), { networkCallTimeStamp: timeStampedPayload.networkCallTimeStamp, cacheTimeStamp: timeStampedPayload.cacheTimeStamp //pattern conforms to Date.toString()
     });
}
export function getUniqueId() {
    let counter = Number(localStorage.getItem('mfe-utils-id-counter') || '0');
    if (counter >= 1000000) {
        counter = 0;
    }
    try {
        localStorage.setItem('mfe-utils-id-counter', String(counter + 1));
    }
    catch (e) {
        console.error('localStorage is not supported in this browser');
        //Let's just use the window instead.
        counter = window['mfe-utils-id-counter'] || 0;
        if (counter >= 1000000) {
            counter = 0;
        }
        window['mfe-utils-id-counter'] = counter + 1;
    }
    return `${Date.now()}-${counter}`;
}
/**
 * TODO: finish this documentation
 *  Prefetch checks if we have the data for a request in the L1 or L2 cache
 * If
 * @param url
 * @returns
 */
function preFetch(url, superFetchTagList) {
    //Ask the service worker if it can deliver a response for that URL.
    if (navigator.serviceWorker.controller == null) {
        console.warn('The Service Worker is not yet registered or it is not supported by this browser');
        //We just return an empty prefetch message.
        return Promise.resolve({
            type: 'PRE_FETCH_RESPONSE',
            case: 'NO_DATA_FROM_PRE_FETCH',
            prefetchPid: '0',
            responseIsStale: false
        });
    }
    else {
        return new Promise((resolve, reject) => {
            var _a;
            //This important for when this gets called and the service worker isn't setup for pre-fetch
            const prefetchPid = getUniqueId(); //Prefetch Process Id
            window.addEventListener('service-worker-message', onPrefetchCallback);
            (_a = navigator.serviceWorker.controller) === null || _a === void 0 ? void 0 : _a.postMessage({
                type: 'PRE_FETCH',
                prefetchPid: prefetchPid,
                url: url,
                superFetchTagList: superFetchTagList
            });
            function onPrefetchCallback(eventData) {
                if (eventData.detail.type === 'PRE_FETCH_RESPONSE') {
                    const prefetchPayload = eventData.detail;
                    if (prefetchPayload.prefetchPid === prefetchPid) {
                        window.removeEventListener('service-worker-message', onPrefetchCallback);
                        resolve(prefetchPayload);
                    }
                }
            }
        });
    }
}
/**
 * Registers the service worker to the browser
 * @param serviceWorkerPath takes the path of the service worker as a string
 */
export function registerServiceWorker(serviceWorkerPath) {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.onmessage = (event) => {
            window.dispatchEvent(new CustomEvent('service-worker-message', { detail: event.data }));
        };
        return navigator.serviceWorker.register(serviceWorkerPath).then((reg) => {
            if (navigator.serviceWorker.controller == null) {
                return reg.unregister().then(() => {
                    return navigator.serviceWorker.register(serviceWorkerPath);
                });
            }
            else {
                return reg;
            }
        });
    }
    else {
        return Promise.reject(new Error('Service workers are not supported in this browser'));
    }
}
/**
 * Sets the stale time and expire time for a specific url or set of url\'s in the service worker and resolves the returned promise when finished.
 * @param urlGlob string of URL to cache or Glob used to match a set of Url's
 * @param stale The minute value of how old the data can be before it is considered stale. Stale data returns from the cache and a network request in the background. The data is updated once the new network call is complete.
 * @param expires The minute value of how old the data can be before it is considered expired. Expired data will never pull from the cache.
 *
 * @returns A promise that resolves to true if successful and false otherwise
 */
export function registerTimeouts(urlGlob, stale, expires) {
    if (navigator.serviceWorker.controller == null) {
        console.warn('The Service Worker is not yet registered or it is not supported by this browser');
        return Promise.resolve(false);
    }
    else {
        if (stale > expires) {
            console.error('service-worker: The value of expire should always be greater than or equal to the value of the stale timeout');
        }
        return new Promise((resolve, reject) => {
            var _a;
            function onUrlRegistrationCallback(eventData) {
                // We compare these three values to make sure we remove the correct listener
                // It's possible this function can be called for the same urlGlob,
                // but if the timeouts are the same then it doesn't matter which listener we remove
                if (eventData.detail.type === 'UPDATED_CACHE_TIMEOUTS'
                    && urlGlob === eventData.detail.urlGlob
                    && stale === eventData.detail.stale
                    && expires === eventData.detail.expires) {
                    window.removeEventListener('service-worker-message', onUrlRegistrationCallback);
                    resolve(true);
                }
            }
            window.addEventListener('service-worker-message', onUrlRegistrationCallback);
            (_a = navigator.serviceWorker.controller) === null || _a === void 0 ? void 0 : _a.postMessage({
                type: 'UPDATE_CACHE_TIMEOUTS',
                // this doesn't need to actually be an array anymore, but keeping it this way to prevent breaks for mfe-utils version mismatches
                timeouts: [
                    {
                        url: urlGlob,
                        urlRegex: globToRegExp(urlGlob, { globstar: true }),
                        timeouts: {
                            'stale': stale,
                            'expires': expires
                        },
                    }
                ]
            });
        });
    }
}
/**
 * Searches both dynamic and static caches that were created by mfe-utils service workers
 * @param urlGlob a glob string or string literal to determine which files to delete.
 * NOTE: that special unicode characters are not allowed in urls and will need to be converted
 * to their respective url percent encoding.
 * Ex: ú = %C3%BA refer to https://r12a.github.io/app-conversion/ after conversion check the section "percent encoding for url's"
 * @returns A promise that resolves to true if no issues occured, false if we were unable to delete a matching cache entry
 * True is returned if no match is found to delete
 */
export function deleteFromCacheByUrlGlob(urlGlob) {
    let isDeleted = true;
    if (navigator.serviceWorker.controller == null) {
        console.warn('The Service Worker is not yet registered or it is not supported by this browser');
        isDeleted = false;
        return Promise.resolve(isDeleted);
    }
    else {
        return new Promise((resolve, reject) => {
            var _a;
            function deletionCompletedCallback(eventData) {
                if (eventData.detail.type === 'DELETED_FROM_DYNAMIC_CACHE') {
                    isDeleted = eventData.detail.isDeleted;
                    window.removeEventListener('service-worker-message', deletionCompletedCallback);
                    resolve(isDeleted);
                }
            }
            window.addEventListener('service-worker-message', deletionCompletedCallback);
            (_a = navigator.serviceWorker.controller) === null || _a === void 0 ? void 0 : _a.postMessage({
                type: 'DELETE_FROM_DYNAMIC_CACHE',
                urlGlob: urlGlob
            });
        });
    }
}
/**
 * Searches both dynamic and static caches that were created by mfe-utils service workers and deletes entries
 * with at least one tag that matches the passed in RegExp.
 * @param tagMatch a regex that describes the tags that you want cache entries to be deleted.
 * @returns A promise that resolves to true if no issues occurred, false if we were unable to delete a matching cache entry
 * True is returned if no match is found to delete
 * True is returned if there is no service worker registered (Which means there isn't anything in the cache)
 */
export function deleteFromCacheByTag(tagMatch) {
    // console.log('deleteFromCacheByTag() called:', tagMatch);
    if (navigator.serviceWorker.controller == null) {
        console.warn('The Service Worker is not yet registered or it is not supported by this browser');
        return Promise.resolve(true);
    }
    navigator.serviceWorker.controller.postMessage({
        type: 'DELETE_CACHE_FOR_TAGS',
        tagMatchRegExpString: tagMatch.source,
        tagMatchRegExpFlags: tagMatch.flags
    });
    return new Promise((resolve) => {
        function deleteFromCacheByTagCallback(eventData) {
            if (eventData.detail.type === 'DELETE_CACHE_FOR_TAGS_RESPONSE') {
                window.removeEventListener('service-worker-message', deleteFromCacheByTagCallback);
                resolve(eventData.detail.isDeleted === true);
            }
        }
        window.addEventListener('service-worker-message', deleteFromCacheByTagCallback);
    });
}
/**
 * Sends a message to the service worker to update the encryption keys variable and returns a promise when done
 * @param encryptionKeys object containing keys following this object pattern:
 * { 'activeKeyId': string, 'keys': Array<{ 'keyId': string, 'value': string }>}
 *
 * @returns a true promise once set or false if it failed
 */
export function setEncryptionKeys(encryptionKeys) {
    if (navigator.serviceWorker.controller == null) {
        console.warn('The Service Worker is not yet registered or it is not supported by this browser');
        return Promise.resolve(false);
    }
    else {
        return new Promise((resolve, reject) => {
            var _a;
            function encryptionKeysUpdatedCallback(eventData) {
                if (eventData.detail.type === 'UPDATED_ENCRYPTION_KEYS') {
                    window.removeEventListener('service-worker-message', encryptionKeysUpdatedCallback);
                    resolve(true);
                }
            }
            window.addEventListener('service-worker-message', encryptionKeysUpdatedCallback);
            (_a = navigator.serviceWorker.controller) === null || _a === void 0 ? void 0 : _a.postMessage({
                type: 'UPDATE_ENCRYPTION_KEYS',
                encryptionKeys: encryptionKeys
            });
        });
    }
}
export function webWorkerTestFunction(value) {
    return `---> ${value} <---`;
}
/**
 * If your MFE needs to call different servers based on which application it is loaded into then
 * each application can set a different prefix for the mfe id.
 * The MFE will call this function to get the prefix that the application has set and then it can
 * make its http calls using the prefix.
 * @param mfeId: Syncs up with ModuleFederation 'name'
 * @returns The MFE url prefix.
 * @deprecated If you need to pass in non-changing data into an MFE, send it through the MfeInitFunctionConfig.
 * See DynamicComponent or initializeMfe() and psMfe() for more information on doing that.  If you need to
 * pass data that might change throughout the lifecycle of your MFE, please use the mfe
 * event functions addMfeEventListener() and dispatchMfeEvent().
 */
export function getMfeUrlPrefix(mfeId) {
    console.warn('getMfeUrlPrefix is deprecated. Please see https://neon.powerschool.com/utils/mfe for alternatives.');
    const mfeUtilsPrefixCache = getUrlPrefixCache();
    if (mfeUtilsPrefixCache[mfeId] == null) {
        mfeUtilsPrefixCache[mfeId] = new ExternalResolvePromise();
    }
    return mfeUtilsPrefixCache[mfeId].promise;
}
/**
 * This function is expected to be called by the application that loads up mfes. It's for setting
 * a url prefix for a specific MFE.  Presumably the MFE would know to call getMfeUrlPrefix() its self
 * and then construct it's API calls using the prefix.
 * @param mfeId: Syncs up with ModuleFederation 'name'
 * @param urlPrefix: A string that represents the url prefix.
 * @deprecated If you need to pass in non-changing data into an MFE, send it through the MfeInitFunctionConfig.
 * See DynamicComponent or initializeMfe() and psMfe() for more information on doing that.  If you need to
 * pass data that might change throughout the lifecycle of your MFE, please use the mfe
 * event functions addMfeEventListener() and dispatchMfeEvent().
 */
export function setMfeUrlPrefix(mfeId, urlPrefix) {
    console.warn('setMfeUrlPrefix is deprecated. Please see https://neon.powerschool.com/utils/mfe for alternatives.');
    const mfeUtilsPrefixCache = getUrlPrefixCache();
    //If no one has asked for this yet, then we create a promise which will be resolved right away (at the end of the method).
    if (mfeUtilsPrefixCache[mfeId] == null) {
        mfeUtilsPrefixCache[mfeId] = new ExternalResolvePromise();
    }
    else if (mfeUtilsPrefixCache[mfeId].isResolved) {
        console.error(`You cannot set the MfeUrlPrefix for the Mfe with id: '${mfeId}' more than once in an application.`);
    }
    if (!mfeUtilsPrefixCache[mfeId].isResolved) {
        mfeUtilsPrefixCache[mfeId].externalResolve(urlPrefix);
    }
}
function getUrlPrefixCache() {
    if (window.mfeUtilsPrefixCache == null) {
        window.mfeUtilsPrefixCache = {};
    }
    return window.mfeUtilsPrefixCache;
}
/**
 * This function avoids making a network call to determine the aged state of cached data.
 *
 * @note: The value of stale, expired, or updated depends on the given stale and expired values when registering the data via the registerCacheTimeouts function
 * updated  =  elapsedTime < expired && elapsedTime < stale minutes old
 * stale    =  elapsedTime > stale && elapsedTime < expired minutes old
 * expired  =  elapsedTime > expired minutes old
 * no data found = we could not determine the cache data status. Refer to logs for more information
 * Ex: stale = 1 expired = 2; updated = less than 1 minute old; stale is between 1 and 2 minutes old; expired is greater than 2 minutes old
 * For more detailed information on what the 3 values above mean refer to the superfetch and registerTimeouts functions documentation
 * @param urlOrGlob: the url that you want to check the cache state for. This can be the URL literal or a urlGlob
 * @param isURLGlob: set to true if using a URL glob, false by default
 * @returns On success there will be an object returned with each URL as a key and the current age status on load
 * On failure the key will be the given urlOrGlob string with an error message
 * If there is currently no data cached, an empty object will be returned.
 * If a certain URL is not cached, the key in the responding promise will not exist.  (So if you get undefined you can assume that it is not cached.)
 */
export function getUrlCacheState(urlOrGlob, isURLGlob = false) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlStatuses = {};
        let dynamicCacheName = '';
        dynamicCacheName = yield getDynamicCacheName();
        if (dynamicCacheName.length === 0) {
            console.error('The service worker cache was not found when using the getUrlCacheState function. This is not expected behavior if the service worker is set up correctly.');
            return {};
        }
        const dynamicCache = yield caches.open(dynamicCacheName);
        // There are more operations needed if it's a glob, so if the flag isn't set lets skip it
        if (isURLGlob) {
            const validUrlsRegex = globToRegExp(urlOrGlob, { globstar: true });
            // Cache keys are the intercepted requests, they contain the URL of the request
            const dynamicCacheKeys = yield dynamicCache.keys();
            // If the cache is empty this will not be run and an empty object will be returned
            for (const request of dynamicCacheKeys) {
                if (validUrlsRegex.test(request.url) === true) {
                    const cachedReponseWithEncryptedPayload = yield dynamicCache.match(request);
                    if (cachedReponseWithEncryptedPayload != null) {
                        const payloadWithStaleAndExpiredValues = yield cachedReponseWithEncryptedPayload.clone().json();
                        urlStatuses[request.url] = getCachedDataStatus(payloadWithStaleAndExpiredValues);
                    }
                }
            }
        }
        else {
            //renaming for readability
            const url = urlOrGlob;
            const cachedReponseWithEncryptedPayload = yield dynamicCache.match(url);
            if (cachedReponseWithEncryptedPayload == null) {
                // This url is not currently cached, so we will return an empty object
                return {};
            }
            const payloadWithStaleAndExpiredValue = yield cachedReponseWithEncryptedPayload.clone().json();
            urlStatuses[url] = getCachedDataStatus(payloadWithStaleAndExpiredValue);
        }
        return urlStatuses;
    });
}
/**
 * This function determines the state of data that has been cached by the service worker
 * @param payloadWithStaleAndExpiredValue: this is the body of the cached response as JSON, it includes the stale and expired values that were give via the registerTimeouts function
 */
function getCachedDataStatus(payloadWithStaleAndExpiredValue) {
    if (isStale(payloadWithStaleAndExpiredValue.cacheTimeStamp, payloadWithStaleAndExpiredValue.stale) === true) {
        if (isExpired(payloadWithStaleAndExpiredValue.cacheTimeStamp, payloadWithStaleAndExpiredValue.expires) === true) {
            return 'expired';
        }
        else {
            return 'stale';
        }
    }
    else {
        return 'updated';
    }
}
